import './i18n';
import React from 'react';
import { BrowserRouter as Router, Route, Switch, Redirect, useLocation } from 'react-router-dom';
import ConnectedStore from './Shared/ConnectedStore';
import { UserReducer, initialState, Provider as UserProvider } from './Shared/UserContext';
import { HelmetProvider } from "react-helmet-async";
import {
  Provider as ProviderConfig,
  initialState as initConfig,
  ConfigReducer,
} from './Shared/ConfigContext';
import ProtectedRoute from './Shared/ProtectedRoute';
import Login from './components/Login';
import Forbidden from './components/Forbidden';
import Clients from './modules/Clients/Clients';
import Products from './modules/Products/Products';
import Utilisateurs from './modules/Users/Utilisateurs';
import ClientDetails from './modules/Clients/ClientDetails';
import ModuleWrapper from './components/ModuleWrapper';
import Brands from './modules/Brands/Brands';
import Families from './modules/Families/Families';
import Orders from './modules/Orders/Orders';
import OrderDetails from './modules/Orders/OrderDetails';
import Repositories from './modules/Repo/Repositories';
import Providers from './modules/Repo/Providers';
import ProductsByRepo from './modules/Repo/ProductsByRepo';
import Purchases from './modules/Purchases/Purchases';
import Transfers from './modules/Repo/Transfers';
import TransferRequests from './modules/Repo/TransferRequests';
import ExtraFields from './modules/Fields/ExtraFields';
import Dashboard from './modules/Dashboard/Dashboard';
import DeliveryNotes from './modules/DeliveryNotes/DeliveryNotes';
import DeliveryNoteDetails from './modules/DeliveryNotes/DeliveryNoteDetails';
import QuoteDetails from './modules/Quotes/QuoteDetails';
import CreditNotes from './modules/CreditNotes/CreditNotes';
import CreditNoteDetails from './modules/CreditNotes/CreditNoteDetails';
import Invoices from './modules/Invoices/Invoices';
import InvoiceDetails from './modules/Invoices/InvoiceDetails';
import Payments from './modules/Recouvrement/Payments';
import InfosFinanciers from './modules/Recouvrement/InfosFinanciers';
import Expenses from './modules/Performance/Expenses';
import Quotes from './modules/Quotes/Quotes';
import AvoirFinanciers from './modules/AvoirFinanciers/AvoirFinanciers';
import AvoirFournisseurs from './modules/AvoirFournisseurs/AvoirFournisseurs';
import AvoirFinancierDetails from './modules/AvoirFinanciers/AvoirFinancierDetails';
import AvoirFournisseursDetails from './modules/AvoirFournisseurs/AvoirFournisseursDetails';
import ExpenseDetails from './modules/Performance/ExpenseDetails';
import PaymentsDetails from './modules/Recouvrement/PaymentsDetails';
import Signup from './components/Login/Signup';
import VerifyEmail from './components/Login/VerifyEmail';
import MobileInterface from './components/MobileInterface';
import Banks from './modules/Banks/Banks';
import PaymentTypes from './modules/PaymentTypes/PaymentsTypes';
import Subscription from './modules/Administration/Subscription';
import BillingInvoices from './modules/Administration/BillingInvoices';
import BillingInvoiceDetails from './modules/Administration/BillingInvoiceDetails';
import './index.css';
import './output.css';


const isMobile = () =>
  /(Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini)/i.test(navigator.userAgent);

const AppRoutes = () => {
  const location = useLocation();
  const onMobile = isMobile();

  if (onMobile) {
    if (location.pathname !== '/signup' && location.pathname !== '/verify-email' && location.pathname !== '/mobile') {
      return <Redirect to="/mobile" />;
    }
    return (
      <Switch>
        <Route path="/verify-email" component={VerifyEmail} />
        <Route path="/signup" component={Signup} exact />
        <Route path="/mobile" component={MobileInterface} />
        <Redirect to="/mobile" />
      </Switch>
    );
  }
  return (
          <Switch>
            <Route path="/login" component={Login} exact />
            <Route path="/signup" component={Signup} exact />
            <Route path="/verify-email" component={VerifyEmail} />
            <Route path="/403" component={Forbidden} exact />


            {/* Dashboard - All roles */}
            <ProtectedRoute path="/" component={Dashboard} exact allowedRoles={['ROLE_ADMIN', 'ROLE_COMMERCIAL', 'ROLE_AGENT']} />
            {/* Catalog - All roles */}
            <ProtectedRoute path="/catalog/brands" component={Brands} exact allowedRoles={['ROLE_ADMIN', 'ROLE_COMMERCIAL', 'ROLE_AGENT']} />
            <ProtectedRoute path="/catalog/families" component={Families} exact allowedRoles={['ROLE_ADMIN', 'ROLE_COMMERCIAL', 'ROLE_AGENT']} />
            <ProtectedRoute path="/catalog/products" component={Products} exact allowedRoles={['ROLE_ADMIN', 'ROLE_COMMERCIAL', 'ROLE_AGENT']} />

            {/* Clients */}
            <ProtectedRoute
              path="/clients/add"
              render={(props) => <ModuleWrapper {...props} moduleType="clients" />}
              allowedRoles={['ROLE_ADMIN', 'ROLE_COMMERCIAL', 'ROLE_AGENT']}
            />
            <ProtectedRoute
              path="/clients/modify/:id"
              render={(props) => <ModuleWrapper {...props} moduleType="clients" mode="modify" />}
              allowedRoles={['ROLE_ADMIN', 'ROLE_COMMERCIAL', 'ROLE_AGENT']}
            />
            <ProtectedRoute
              path="/clients/details/modify/:id"
              render={(props) => <ModuleWrapper {...props} moduleType="clients" mode="modify" />}
              allowedRoles={['ROLE_ADMIN', 'ROLE_COMMERCIAL', 'ROLE_AGENT']}
            />
            <ProtectedRoute path="/clients/details/:id" component={ClientDetails} exact allowedRoles={['ROLE_ADMIN', 'ROLE_COMMERCIAL', 'ROLE_AGENT']} />
            <ProtectedRoute path="/clients" component={Clients} exact allowedRoles={['ROLE_ADMIN', 'ROLE_COMMERCIAL', 'ROLE_AGENT']} />
            {/* Purchases */}
            <ProtectedRoute
              path="/purchases/add"
              render={(props) => <ModuleWrapper {...props} moduleType="purchases" />}
              allowedRoles={['ROLE_ADMIN', 'ROLE_AGENT']}
            />
            <ProtectedRoute path="/purchases" component={Purchases} exact allowedRoles={['ROLE_ADMIN', 'ROLE_AGENT']} />

            {/* Quotes */}
            <ProtectedRoute
              path="/quotes/add"
              render={(props) => <ModuleWrapper {...props} moduleType="quotes" />}
              allowedRoles={['ROLE_ADMIN', 'ROLE_COMMERCIAL']}
            />
            <ProtectedRoute
              path="/quotes/details/modify/:id"
              render={(props) => <ModuleWrapper {...props} moduleType="quotes" mode="modify" />}
              allowedRoles={['ROLE_ADMIN', 'ROLE_COMMERCIAL']}
            />
            <ProtectedRoute
              path="/quotes/modify/:id"
              render={(props) => <ModuleWrapper {...props} moduleType="quotes" mode="modify" />}
              allowedRoles={['ROLE_ADMIN', 'ROLE_COMMERCIAL']}
            />
            <ProtectedRoute path="/quotes/details/:id" component={QuoteDetails} exact allowedRoles={['ROLE_ADMIN', 'ROLE_AGENT', 'ROLE_COMMERCIAL']} />
            <ProtectedRoute path="/quotes" component={Quotes} allowedRoles={['ROLE_ADMIN', 'ROLE_AGENT', 'ROLE_COMMERCIAL']} />

            {/* Orders */}
            <ProtectedRoute path="/orders/details/:id" component={OrderDetails} exact allowedRoles={['ROLE_ADMIN', 'ROLE_AGENT', 'ROLE_COMMERCIAL']} />
            <ProtectedRoute
              path="/orders/add"
              render={(props) => <ModuleWrapper {...props} moduleType="orders" />}
              allowedRoles={['ROLE_ADMIN', 'ROLE_COMMERCIAL']}
            />
            <ProtectedRoute
              path="/orders/details/modify/:id"
              render={(props) => <ModuleWrapper {...props} moduleType="orders" mode="modify" />}
              allowedRoles={['ROLE_ADMIN', 'ROLE_COMMERCIAL']}
            />
            <ProtectedRoute
              path="/orders/modify/:id"
              render={(props) => <ModuleWrapper {...props} moduleType="orders" mode="modify" />}
              allowedRoles={['ROLE_ADMIN', 'ROLE_COMMERCIAL']}
            />
            <ProtectedRoute path="/orders" component={Orders} exact allowedRoles={['ROLE_ADMIN', 'ROLE_AGENT', 'ROLE_COMMERCIAL']} />

            {/* Delivery Notes */}
            <ProtectedRoute path="/delivery-notes/details/:id" component={DeliveryNoteDetails} exact allowedRoles={['ROLE_ADMIN', 'ROLE_AGENT', 'ROLE_COMMERCIAL']} />
            <ProtectedRoute path="/delivery-notes" component={DeliveryNotes} exact allowedRoles={['ROLE_ADMIN', 'ROLE_AGENT', 'ROLE_COMMERCIAL']} />

            {/* Invoices */}
            <ProtectedRoute path="/invoices/details/:id" component={InvoiceDetails} exact allowedRoles={['ROLE_ADMIN', 'ROLE_AGENT', 'ROLE_COMMERCIAL']} />
            <ProtectedRoute path="/invoices" component={Invoices} exact allowedRoles={['ROLE_ADMIN', 'ROLE_AGENT', 'ROLE_COMMERCIAL']} />


            {/* Credit Notes */}
            <ProtectedRoute
              path="/credit-notes/add"
              render={(props) => <ModuleWrapper {...props} moduleType="creditNotes" />}
              allowedRoles={['ROLE_ADMIN', 'ROLE_COMMERCIAL']}
            />
            <ProtectedRoute path="/credit-notes/details/:id" component={CreditNoteDetails} exact allowedRoles={['ROLE_ADMIN', 'ROLE_AGENT', 'ROLE_COMMERCIAL']} />
            <ProtectedRoute path="/credit-notes" component={CreditNotes} exact allowedRoles={['ROLE_ADMIN', 'ROLE_AGENT', 'ROLE_COMMERCIAL']} />

            {/* Avoirs Financiers */}
            <ProtectedRoute
              path="/avoirs-financiers/add"
              render={(props) => <ModuleWrapper {...props} moduleType="afinancier" />}
              allowedRoles={['ROLE_ADMIN', 'ROLE_AGENT', 'ROLE_COMMERCIAL']}
            />
            <ProtectedRoute
              path="/avoirs-financiers/details/modify/:id"
              render={(props) => <ModuleWrapper {...props} moduleType="afinancier" mode="modify" />}
              allowedRoles={['ROLE_ADMIN', 'ROLE_AGENT', 'ROLE_COMMERCIAL']}
            />
            <ProtectedRoute path="/avoirs-financiers/details/:id" component={AvoirFinancierDetails} allowedRoles={['ROLE_ADMIN', 'ROLE_AGENT', 'ROLE_COMMERCIAL']} />
            <ProtectedRoute path="/avoirs-financiers" component={AvoirFinanciers} allowedRoles={['ROLE_ADMIN', 'ROLE_AGENT', 'ROLE_COMMERCIAL']} />

            {/* Avoirs Fournisseurs */}
            <ProtectedRoute
              path="/avoirs-fournisseurs/add"
              render={(props) => <ModuleWrapper {...props} moduleType="afournisseur" />}
              allowedRoles={['ROLE_ADMIN', 'ROLE_AGENT']}
            />
            <ProtectedRoute
              path="/avoirs-fournisseurs/modify/:id"
              render={(props) => <ModuleWrapper {...props} moduleType="afournisseur" mode="modify" />}
              allowedRoles={['ROLE_ADMIN', 'ROLE_AGENT']}
            />
            <ProtectedRoute path="/avoirs-fournisseurs/details/:id" component={AvoirFournisseursDetails} allowedRoles={['ROLE_ADMIN', 'ROLE_AGENT']} />
            <ProtectedRoute path="/avoirs-fournisseurs" component={AvoirFournisseurs} allowedRoles={['ROLE_ADMIN', 'ROLE_AGENT']} />

            {/* Payments / Recouvrement */}
            <ProtectedRoute
              path="/payments/add"
              render={(props) => <ModuleWrapper {...props} moduleType="payments" />}
              allowedRoles={['ROLE_ADMIN', 'ROLE_AGENT', 'ROLE_COMMERCIAL']}
            />
            <ProtectedRoute
              path="/payments/details/modify/:id"
              render={(props) => <ModuleWrapper {...props} moduleType="payments" mode="modify" />}
              allowedRoles={['ROLE_ADMIN', 'ROLE_AGENT', 'ROLE_COMMERCIAL']}
            />
            <ProtectedRoute
              path="/payments/modify/:id"
              render={(props) => <ModuleWrapper {...props} moduleType="payments" mode="modify" />}
              allowedRoles={['ROLE_ADMIN', 'ROLE_AGENT', 'ROLE_COMMERCIAL']}
            />
            <ProtectedRoute path="/payments/details/:id" component={PaymentsDetails} exact allowedRoles={['ROLE_ADMIN', 'ROLE_AGENT', 'ROLE_COMMERCIAL']} />
            <ProtectedRoute path="/payments" component={Payments} exact allowedRoles={['ROLE_ADMIN', 'ROLE_AGENT', 'ROLE_COMMERCIAL']} />
            <ProtectedRoute path="/infos-financiers" component={InfosFinanciers} exact allowedRoles={['ROLE_ADMIN', 'ROLE_AGENT', 'ROLE_COMMERCIAL']} />

            {/* Expenses */}
            <ProtectedRoute
              path="/expenses/add"
              render={(props) => <ModuleWrapper {...props} moduleType="expenses" />}
              allowedRoles={['ROLE_ADMIN', 'ROLE_COMMERCIAL']}
            />
            <ProtectedRoute
              path="/expenses/details/modify/:id"
              render={(props) => <ModuleWrapper {...props} moduleType="expenses" mode="modify" />}
              allowedRoles={['ROLE_ADMIN', 'ROLE_COMMERCIAL']}
            />
            <ProtectedRoute
              path="/expenses/modify/:id"
              render={(props) => <ModuleWrapper {...props} moduleType="expenses" mode="modify" />}
              allowedRoles={['ROLE_ADMIN', 'ROLE_COMMERCIAL']}
            />
            <ProtectedRoute path="/expenses/details/:id" component={ExpenseDetails} exact allowedRoles={['ROLE_ADMIN', 'ROLE_AGENT', 'ROLE_COMMERCIAL']} />
            <ProtectedRoute path="/expenses" component={Expenses} exact allowedRoles={['ROLE_ADMIN', 'ROLE_AGENT', 'ROLE_COMMERCIAL']} />

            {/* Repo / Stock */}
            <ProtectedRoute path="/products-by-repo" component={ProductsByRepo} exact allowedRoles={['ROLE_ADMIN', 'ROLE_AGENT', 'ROLE_COMMERCIAL']} />
            <ProtectedRoute path="/transfers/history" component={Transfers} exact allowedRoles={['ROLE_ADMIN', 'ROLE_COMMERCIAL']} />
            <ProtectedRoute
              path="/transfer-requests/add"
              render={(props) => <ModuleWrapper {...props} moduleType="requests" />}
              allowedRoles={['ROLE_ADMIN', 'ROLE_COMMERCIAL']}
            />
            <ProtectedRoute
              path="/transfer-requests/modify/:id"
              render={(props) => <ModuleWrapper {...props} moduleType="requests" mode="modify" />}
              allowedRoles={['ROLE_ADMIN', 'ROLE_COMMERCIAL']}
            />
            <ProtectedRoute path="/transfer-requests" component={TransferRequests} exact allowedRoles={['ROLE_ADMIN', 'ROLE_AGENT', 'ROLE_COMMERCIAL']} />
            <ProtectedRoute path="/repos" component={Repositories} exact allowedRoles={['ROLE_ADMIN']} />
            <ProtectedRoute path="/providers" component={Providers} exact allowedRoles={['ROLE_ADMIN']} />

            {/* Administration */}
            <ProtectedRoute path="/users" component={Utilisateurs} exact allowedRoles={['ROLE_ADMIN']} />
            <ProtectedRoute path="/fields" component={ExtraFields} exact allowedRoles={['ROLE_ADMIN']} />
            <ProtectedRoute path="/banks" component={Banks} exact allowedRoles={['ROLE_ADMIN']} />
            <ProtectedRoute path="/payment-types" component={PaymentTypes} exact allowedRoles={['ROLE_ADMIN']} />
            <ProtectedRoute path="/administration/subscription" component={Subscription} exact allowedRoles={['ROLE_ADMIN']} />
            <ProtectedRoute path="/administration/billing-invoices/details/:id" component={BillingInvoiceDetails} exact allowedRoles={['ROLE_ADMIN']} />
            <ProtectedRoute path="/administration/billing-invoices" component={BillingInvoices} exact allowedRoles={['ROLE_ADMIN']} />
          </Switch>
        );
}

const App = () => {
  return (
    <HelmetProvider>
      <Router>
        <div>
          <AppRoutes />
        </div>
      </Router>
    </HelmetProvider>
  );
};

const ConnectedToUserContext = ConnectedStore(App, UserReducer, initialState, UserProvider);
export default ConnectedStore(ConnectedToUserContext, ConfigReducer, initConfig, ProviderConfig);
